import { Loader2 } from "lucide-react";

// ─── Pane Skeletons ───────────────────────────────────────────────────────────

const FILE_WIDTHS = ["w-[72%]", "w-[85%]", "w-[58%]", "w-[78%]", "w-[64%]", "w-[90%]"];

// Commit pane: header (title + meta + buttons) + file list column + diff area
export function CommitPaneSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3.5 w-3/4 animate-pulse rounded-full bg-muted/50" />
          <div className="flex items-center gap-3">
            <div className="h-2.5 w-10 animate-pulse rounded-full bg-muted/30" />
            <div className="h-2.5 w-20 animate-pulse rounded-full bg-muted/30" />
            <div className="h-2.5 w-16 animate-pulse rounded-full bg-muted/30" />
            <div className="h-2.5 w-8 animate-pulse rounded-full bg-green-500/20" />
            <div className="h-2.5 w-8 animate-pulse rounded-full bg-red-500/20" />
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <div className="h-8 w-8 animate-pulse rounded-lg bg-muted/40" />
          <div className="h-8 w-8 animate-pulse rounded-lg bg-muted/40" />
        </div>
      </div>
      {/* Body: file list + diff area */}
      <div className="flex flex-1 min-h-0">
        <div className="w-56 shrink-0 border-r border-border overflow-hidden">
          {FILE_WIDTHS.map((w, i) => (
            <div key={i} className="border-b border-border/50 px-3 py-2.5" style={{ opacity: Math.max(0.2, 1 - i * 0.13) }}>
              <div className={`h-2.5 animate-pulse rounded-full bg-muted/40 ${w}`} />
            </div>
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-2 p-5 opacity-30">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`h-2 animate-pulse rounded-full bg-muted/50 ${["w-4/5","w-2/3","w-[90%]","w-1/2","w-3/4","w-[85%]","w-3/5","w-[70%]"][i]}`} style={{ opacity: 1 - i * 0.1 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// PR pane: header (badge + title + meta + tabs + merge button) + file list + diff area
export function PrPaneSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-muted/20 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-5 w-10 animate-pulse rounded-full bg-muted/40" />
              <div className="h-3.5 w-3/4 animate-pulse rounded-full bg-muted/50" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2.5 w-8 animate-pulse rounded-full bg-muted/30" />
              <div className="h-2.5 w-20 animate-pulse rounded-full bg-muted/30" />
              <div className="h-2.5 w-16 animate-pulse rounded-full bg-muted/30" />
              <div className="h-4 w-20 animate-pulse rounded bg-muted/30" />
              <div className="h-2.5 w-4 animate-pulse rounded-full bg-muted/20" />
              <div className="h-4 w-16 animate-pulse rounded bg-muted/30" />
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <div className="h-8 w-8 animate-pulse rounded-lg bg-muted/40" />
            <div className="h-8 w-8 animate-pulse rounded-lg bg-muted/40" />
          </div>
        </div>
        {/* Tabs + merge row */}
        <div className="mt-3 flex items-center justify-between">
          <div className="h-7 w-36 animate-pulse rounded-lg bg-muted/30" />
          <div className="h-7 w-28 animate-pulse rounded-lg bg-muted/30" />
        </div>
      </div>
      {/* Body */}
      <div className="flex flex-1 min-h-0">
        <div className="w-56 shrink-0 border-r border-border overflow-hidden">
          {FILE_WIDTHS.map((w, i) => (
            <div key={i} className="border-b border-border/50 px-3 py-2.5" style={{ opacity: Math.max(0.2, 1 - i * 0.13) }}>
              <div className={`h-2.5 animate-pulse rounded-full bg-muted/40 ${w}`} />
            </div>
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-2 p-5 opacity-30">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`h-2 animate-pulse rounded-full bg-muted/50 ${["w-3/4","w-[90%]","w-1/2","w-[80%]","w-2/3","w-[88%]","w-3/5","w-[75%]"][i]}`} style={{ opacity: 1 - i * 0.1 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Branch pane — just the "latest commit" inline section skeleton
export function CommitInlineSkeleton() {
  return (
    <div className="space-y-1.5">
      <div className="h-3 w-3/4 animate-pulse rounded-full bg-muted/40" />
      <div className="flex items-center gap-1.5">
        <div className="h-2 w-10 animate-pulse rounded-full bg-muted/25" />
        <div className="h-1 w-1 rounded-full bg-muted/20" />
        <div className="h-2 w-16 animate-pulse rounded-full bg-muted/25" />
        <div className="h-1 w-1 rounded-full bg-muted/20" />
        <div className="h-2 w-12 animate-pulse rounded-full bg-muted/25" />
      </div>
    </div>
  );
}

// ─── Shared Primitive Components ──────────────────────────────────────────────

export function PrStateBadge({ state }: { state: string }) {
  const map: Record<string, string> = {
    open: "bg-green-500/10 text-green-400",
    closed: "bg-muted text-muted-foreground",
    merged: "bg-purple-500/10 text-purple-400",
  };
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${map[state] ?? map.closed}`}>
      {state}
    </span>
  );
}

export function MergeableBadge({ state }: { state: string | null }) {
  if (!state || state === "unknown") return null;
  const map: Record<string, string> = {
    clean: "bg-green-500/10 text-green-400",
    dirty: "bg-yellow-500/10 text-yellow-400",
    blocked: "bg-red-500/10 text-red-400",
    behind: "bg-blue-500/10 text-blue-400",
    draft: "bg-muted text-muted-foreground",
  };
  const labels: Record<string, string> = {
    clean: "mergeable", dirty: "conflicts", blocked: "blocked",
    behind: "behind", draft: "draft",
  };
  const cls = map[state] ?? "bg-muted text-muted-foreground";
  const label = labels[state] ?? state;
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

const SKELETON_WIDTHS = ["w-4/5", "w-3/4", "w-[85%]", "w-2/3", "w-4/5", "w-3/5", "w-3/4", "w-[88%]"];

export function ListSkeleton() {
  return (
    <div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="border-b border-border/50 px-3 py-3 last:border-0"
          style={{ opacity: Math.max(0.2, 1 - i * 0.1) }}
        >
          <div className={`mb-2 h-2.5 animate-pulse rounded-full bg-muted/40 ${SKELETON_WIDTHS[i]}`} />
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-10 animate-pulse rounded-full bg-muted/25" />
            <div className="h-1 w-1 rounded-full bg-muted/15" />
            <div className="h-1.5 w-14 animate-pulse rounded-full bg-muted/25" />
            <div className="h-1 w-1 rounded-full bg-muted/15" />
            <div className="h-1.5 w-12 animate-pulse rounded-full bg-muted/25" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyList({ text }: { text: string }) {
  return <p className="px-4 py-8 text-center text-xs text-muted-foreground">{text}</p>;
}

export function PaneLoader() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

export function PaneEmpty({ text }: { text: string }) {
  return (
    <div className="flex flex-1 items-center justify-center py-16 text-sm text-muted-foreground">
      {text}
    </div>
  );
}

export function StatCard({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={["truncate text-sm font-semibold", mono ? "font-mono" : ""].join(" ")}>{value}</div>
    </div>
  );
}
